export const ACP_REGISTRY_AGENT_PREFIX = 'registry:' as const;
export const ACP_AGENT_PROVIDER_CONTRACT = 'acp-agent.provider.v1' as const;

/** Opaque producer-defined identity used only to match the same agent across runtimes. */
export type AcpKnownAgentFamily = string;

export type AcpAgentCatalogEntry = {
  /** Plugin that owns installation, discovery and launch metadata. */
  plugin: string;
  localId: `auto-${string}`;
  id: string;
  name: string;
  default: boolean;
  family: AcpKnownAgentFamily | null;
  discovery: { binaries: readonly string[] };
  launch: {
    args: readonly string[];
    /** Installed by the host when none of `discovery.binaries` is: an npm package and its bin. */
    npm?: { package: string; bin?: string };
    /** A value naming a program (`codex`) is that program as the host resolves it. */
    env?: Readonly<Record<string, string>>;
  };
  aliases: readonly string[];
  model?: { target: 'session' } | { target: 'env'; name: string };
  sessionConfig: readonly {
    aliases: readonly string[];
    target: string;
    choices?: readonly string[];
  }[];
  modelCompatibility?: {
    includePrefixes?: readonly string[];
    excludePrefixes?: readonly string[];
  };
  providerInjection?: {
    baseUrl:
      | { target: 'env'; name: string; appendPath?: string }
      | { target: 'argument'; name: string; appendPath?: string };
    auth: { target: 'env'; name: string };
    /**
     * Set with the credentials: what tells the agent to sign in with them. `{baseUrl}` and
     * `{token}` stand for them, escaped as in a JSON string.
     */
    env?: Readonly<Record<string, string>>;
  };
  capabilities: readonly string[];
  /** The terminal command that signs the agent in to its service, offered when it is signed out. */
  signIn?: { command: string };
};

import { ACP_AGENT_CATALOG_GENERATED } from './acp-agent-catalog.generated';
import { CORE_ACP_AGENTS } from './core-agents';

/** The core's own agents, then those generated from bundled plugin manifests. */
export const ACP_AGENT_CATALOG: readonly AcpAgentCatalogEntry[] = [
  ...CORE_ACP_AGENTS,
  ...ACP_AGENT_CATALOG_GENERATED,
];
/** Defaults are produced by installed manifests rather than fixed in the ACP consumer. */
export const DEFAULT_ACP_AGENT_IDS: readonly string[] = ACP_AGENT_CATALOG.filter(
  (entry) => entry.default,
).map((entry) => entry.id);

function withoutRegistryPrefix(id: string): string {
  return id.startsWith(ACP_REGISTRY_AGENT_PREFIX) ? id.slice(ACP_REGISTRY_AGENT_PREFIX.length) : id;
}

function commandName(command: string | null | undefined): string {
  return command?.replaceAll('\\', '/').split('/').at(-1)?.toLowerCase() ?? '';
}

export function acpAgentCatalogEntryForId(id: string): AcpAgentCatalogEntry | null {
  const normalized = withoutRegistryPrefix(id);
  return (
    ACP_AGENT_CATALOG.find(
      (entry) =>
        entry.localId === normalized ||
        entry.id === normalized ||
        entry.family === normalized ||
        entry.discovery.binaries.includes(normalized),
    ) ?? null
  );
}

export function acpAgentCatalogEntry(input: {
  id: string;
  command?: string | null;
}): AcpAgentCatalogEntry | null {
  const byId = acpAgentCatalogEntryForId(input.id);
  if (byId !== null) return byId;
  const binary = commandName(input.command);
  return ACP_AGENT_CATALOG.find((entry) => entry.discovery.binaries.includes(binary)) ?? null;
}

export function canonicalAcpAgentId(id: string): string {
  const normalized = withoutRegistryPrefix(id);
  return acpAgentCatalogEntryForId(normalized)?.id ?? normalized;
}

export function knownAcpAgentFamily(id: string): AcpKnownAgentFamily | null {
  return acpAgentCatalogEntryForId(id)?.family ?? null;
}

export function localAcpAgentRegistryId(id: string): string | null {
  return ACP_AGENT_CATALOG.find((entry) => entry.localId === id)?.id ?? null;
}

export function acpAgentCatalogEntryForAlias(alias: string): AcpAgentCatalogEntry | null {
  return ACP_AGENT_CATALOG.find((entry) => entry.aliases.includes(alias)) ?? null;
}

export function acpAgentModelRequest(
  id: string,
  modelId: string | undefined,
): { env?: Record<string, string>; modelId?: string } {
  if (modelId === undefined) return {};
  const model = acpAgentCatalogEntryForId(id)?.model;
  return model?.target === 'env' ? { env: { [model.name]: modelId } } : { modelId };
}

export function normalizeAcpSessionConfig(
  id: string,
  values: Readonly<Record<string, string | boolean>> | undefined,
): Readonly<Record<string, string | boolean>> | undefined {
  if (values === undefined) return undefined;
  const mappings = acpAgentCatalogEntryForId(id)?.sessionConfig ?? [];
  const normalized = { ...values };
  for (const mapping of mappings) {
    const value = mapping.aliases.map((alias) => values[alias]).find((item) => item !== undefined);
    if (value === undefined) continue;
    for (const alias of mapping.aliases) delete normalized[alias];
    normalized[mapping.target] = value;
  }
  return normalized;
}

export function acpAgentSupportsModel(id: string, modelId: string): boolean {
  const compatibility = acpAgentCatalogEntryForId(id)?.modelCompatibility;
  if (!compatibility) return true;
  if (compatibility.includePrefixes?.length)
    return compatibility.includePrefixes.some((prefix) => modelId.startsWith(prefix));
  return !(compatibility.excludePrefixes ?? []).some((prefix) => modelId.startsWith(prefix));
}
